import Moment from "moment";
import "moment/locale/nl";
import { FavoriteTender } from "./FavoriteTender";
import { Link } from "react-router-dom";
import { FiBriefcase } from "react-icons/fi";
import { GrAlarm } from "react-icons/gr";
import { FaRegClock, FaRegFile } from "react-icons/fa6";
import { CardSkeleton } from "./CardSkeleton";
import { GoGear } from "react-icons/go";
import clsx from "clsx";

Moment.locale("nl");

interface CardProps {
    item: {
        id: string;
        title: string;
        title_eng: string;
        dispatch_notice_date: string;
        phase: string;
        country: string;
        date_receipt: string;
        time_receipt: string;
        description: string;
        procedure: string;
        contracting_authority: string;
        tender_type: string;
        tender_id: string;
        contacts: any;
        end_date: string;
    }
    favorite?: boolean;
    companyID?: string;
    showFavoriteIcon?: boolean;
    showPing?: boolean;
    isLoading?: boolean;
}

export const Card = ({
    item,
    showFavoriteIcon = true,
    favorite = false,
    companyID = "",
    showPing = true,
    isLoading = false
}: CardProps) => {
    // If loading, render the skeleton
    if (isLoading) {
        return <CardSkeleton />;
    }

    const dispatch_notice_date = Moment(
        item.dispatch_notice_date?.toString()
    ).format("LL");

    const end_date = Moment(
        item.end_date?.toString()
    ).format("LL");
    
    // Determine color based on end_date
    const getStatusColor = () => {
        if (!item.end_date) {
            // If end_date not available, use current logic
            if (item.phase.toLowerCase().includes("gegunde opdracht") || 
                item.phase.toLowerCase().includes("wijziging")) {
                return "bg-primary-pink";
            } else if (item.phase.toLowerCase().includes("vooraankondiging")) {
                return "bg-[#FFBF00]";
            } else {
                return "bg-primary-green";
            }
        }
        
        const today = Moment();
        const endDateMoment = Moment(item.end_date);
        const daysUntilEnd = endDateMoment.diff(today, 'days');
        
        if (endDateMoment.isBefore(today)) {
            return "bg-red-500"; // Passed -> red
        } else if (daysUntilEnd <= 14) {
            return "bg-orange-500"; // Within 14 days -> orange
        } else {
            return "bg-green-500"; // More than 14 days -> green
        }
    };

    // Process the description - clean it and check length
    const processedDescription = item.description 
        ? item.description.replace(/[\[\]']+/g, "") 
        : "item.description";
    
    // Function to truncate text without cutting off words
    const truncateText = (text: string, maxLength: number): string => {
        if (text.length <= maxLength) return text;
        const lastSpaceIndex = text.lastIndexOf(' ', maxLength);
        const truncateIndex = lastSpaceIndex > 0 ? lastSpaceIndex : maxLength;
        return `${text.substring(0, truncateIndex)}...`;
    };
    
    // Adjust description length based on screen size
    const displayDescription = truncateText(processedDescription, 200);

    return (
        <div key={item.id} className="rounded-lg bg-white mx-2 sm:mx-3 md:mx-5 mb-4 sm:mb-5 cursor-pointer border shadow hover:shadow-xl relative">
            {showFavoriteIcon && (
                <FavoriteTender
                    tenderId={item.tender_id}
                    companyID={companyID}
                    initialFavorite={favorite}
                    className="absolute right-3 sm:right-5 top-3 sm:top-5"
                />
            )}

            <Link to={`/tender/${item.tender_id}/${item.id}`}>
                <div className="p-3 sm:p-4 md:pt-5 md:px-5 md:pb-2 border-b flex flex-col">
                    <p className="text-primary-pink w-[95%]">
                        {item.phase ? (
                            <div className="flex items-center">
                                {showPing && (
                                    <span className="relative flex h-3 w-3 mr-2">
                                        <span
                                            className={clsx(
                                                getStatusColor(),
                                                "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                                            )}
                                        />
                                        <span
                                            className={clsx(
                                                getStatusColor(),
                                                "relative inline-flex rounded-full h-3 w-3"
                                            )}
                                        ></span>
                                    </span>
                                )}
                                <span className="text-sm sm:text-base">{item.phase}</span>
                            </div>
                            
                            
                        ) : (
                            <span className="text-sm sm:text-base">Onbekend</span>
                        )}
                    </p>
                    <span className="font-bold text-base sm:text-lg pt-1 line-clamp-2">
                        {item.title}
                    </span>
                    <div className="flex items-center w-full text-primary-blue pt-1">
                        <FiBriefcase className="text-base sm:text-lg flex-shrink-0" />
                        <p className="pl-1 text-xs sm:text-sm truncate w-full">
                            {item.contracting_authority}
                        </p>
                    </div>
                </div>

                <div className="p-3 sm:p-4 md:px-5 md:pb-5 md:pt-2 flex flex-col md:flex-row w-full">
                    <div className="w-full md:w-[65%] md:pr-5 flex border-b md:border-r md:border-b-0 pb-3 md:pb-0">
                        <span className="text-sm sm:text-base">
                            {displayDescription}
                        </span>
                    </div>
                    <div className="flex flex-col w-full md:w-[35%] pt-3 md:pt-0 md:pl-5">
                        <div className="flex justify-between mb-1 sm:mb-2">
                            <div className="flex items-center">
                                <FaRegClock className="text-sm sm:text-base flex-shrink-0" />
                                <span className="font-bold pl-1 text-xs sm:text-sm">
                                    Publicatiedatum:
                                </span>
                            </div>
                            <div>
                                {item.dispatch_notice_date ? (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">{dispatch_notice_date}</p>
                                ) : (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">Onbekend</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between mb-1 sm:mb-2">
                            <div className="flex items-center">
                                <GrAlarm className="text-sm sm:text-base flex-shrink-0" />
                                <span className="font-bold pl-1 text-xs sm:text-sm">
                                    Sluitingsdatum:
                                </span>
                            </div>
                            <div>
                                {item.end_date ? (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">{end_date}</p>
                                ) : (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">Onbekend</p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between mb-1 sm:mb-2">
                            <div className="flex items-center">
                                <FaRegFile className="text-sm sm:text-base flex-shrink-0" />
                                <span className="font-bold pl-1 text-xs sm:text-sm">
                                    Procedure:
                                </span>
                            </div>
                            <div>
                                {item.procedure === "" ||
                                item.procedure === "-" ? (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">
                                        Onbekend
                                    </p>
                                ) : (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">
                                        {item.procedure}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between">
                            <div className="flex items-center">
                                <GoGear className="text-sm sm:text-base flex-shrink-0" />
                                <span className="font-bold pl-1 text-xs sm:text-sm">
                                    Type:
                                </span>
                            </div>
                            <div>
                                {item.tender_type === "" ||
                                item.tender_type === "-" ? (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">
                                        Onbekend
                                    </p>
                                ) : (
                                    <p className="text-right opacity-60 text-xs sm:text-sm">
                                        {item.tender_type}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};

export const CardSkeletonArray = ({ count = 3 }: { count?: number }) => {
    return (
        <>
            {Array(count).fill(0).map((_, index) => (
                <CardSkeleton key={index} />
            ))}
        </>
    );
};